// Things in the sim (gate C5): gathering, breakables, pickups, interaction and the navmesh's tile rebuilds, as one
// kernel module on a scene with the hero on @rpg/controller and the joined fight (src/enemies/fight.ts). Every thing is
// a placement of its kind (seam 5: `{ id, kind, piece, pos, yaw, seed }`), its state keyed by the placement id.
//
// One sim step, by system order in the one kernel game:
//   things:clock     (-40)  the things' sim clock
//   things:regrow    (-30)  gathering nodes whose timer has run out grow back (@rpg/items, sim time)
//   things:interact  (-20)  a gather in progress finishes (its yield into the bag) or holds the hero; else the
//                           interaction chooser (@rpg/controller) picks what the prompt shows from the hero's feet and
//                           facing, and an interact press fires it: a herb, flower or ore starts a gather (a pose
//                           through the bodies module's track, the hero held facing it), a sign is read, a door opens
//                           or closes (its collider and its nav shape go or come back), a lever is pulled
//   (fight:act -10, the controllers 0)
//   things:pickups   (20)   pickups the hero walks over go into the bag (@rpg/items; what doesn't fit stays)
//   (fight:move 50, physics 100, the bodies' ragdolls 110, fight:resolve 150: a blow on a breakable's hurt sensor is a
//    `combat:hit`, its health of 1 makes it `combat:defeated`)
//   things:break     (160)  breakables hit or struck past their impact threshold (the collision world's impacts) shatter:
//                           the body and its combat target go, its pre-cut fragments (@rpg/meshkit, seeded by the
//                           placement) become rigid bodies flung from the blow, its drop table spawns pickups, and when
//                           it blocked the navmesh its tiles are queued for a rebuild; fragments settle, fade and go
//   things:nav       (170)  the tile slicer (@rpg/nav) runs one slice: a few units of tile work, the tile in use kept
//                           until the new one swaps in
// In a streamed world (`chunkOf` given: the default scene) a breakable's body and combat target, and a door's slab,
// load and unload with their chunk on `world:chunk` (fragments flying in a chunk that unloads go with it); the
// interaction registry holds every placement (the chooser only ever finds the near ones).
// State: one kernel component (`things/State`: the clock, gathering, the bag, pickups, broken placements, fragments,
// the interaction's choice, doors and levers, the gather in progress, the navmesh's dynamic state and the slicer's
// place), saved and hashed; the fragments' bodies are in the collision world's snapshot, the breakables' combat
// targets in the fight's.
import type { GameModule, World } from "@rpg/kernel";
import type { Actions, RngStreams } from "@rpg/runtime";
import type { BodySpec, CollisionWorld, ColliderSpec, Quat } from "@rpg/collision";
import { Interaction, type ControllerState, type Interactable, type InteractChoice, type InteractionState, type MoveDrive } from "@rpg/controller";
import { interactDone, interactStep, type InteractEmit } from "@rpg-unit/interact-prompt";
import { NavTileSlicer, type NavDynamicState, type NavShape, type NavWorld, type SlicerState } from "@rpg/nav";
import { collectPickups, createInventory, emptyInventory, emptyPickups, rollDrops, spawnPickups, type Gathered, type GatherState, type Inventory, type InventoryState, type PickupState } from "@rpg/items";
import type { EventBus } from "@rpg/kernel";
import { boxPoints, fracture, ringPoints, type Fragment } from "@rpg/meshkit";
import type { GameEvents } from "../core";
import { HERO_ID, type Fight } from "../enemies/fight";
import type { BreakableKind, Placement, ThingsContent } from "./content";

export const THINGS_C = "things/State";
/** The layers the things add: breakables' hurt sensors (team "prop"), and the debris their fragments become. */
export const THINGS_LAYERS = { hurtProp: [], debris: ["terrain", "solid", "debris"] };
/** Breakables' fighter ids start here (the hero is 0, enemies count up from 1). */
export const BREAKABLE_IDS = 100_000;

/** Planted defects the things own (switched beside what they break each step). */
export const THINGS_FAULTS = {
  /** The tile rebuild runs in one slice (`@rpg/nav` `NavTileSlicer.faults.oneSlice`): the 2 ms per-frame gate fails. */
  navOneSlice: false,
  /** The interaction chooser ignores priority (`@rpg/controller` `Interaction.faults.ignorePriority`). */
  interactIgnorePriority: false,
  /** Gathering nodes never grow back (`@rpg/items` `Gathering.faults.neverRegrow`). */
  gatherNeverRegrow: false,
};
export type ThingsFaultName = keyof typeof THINGS_FAULTS;

/** One fragment in flight or settling. */
export interface FragmentState {
  body: string;
  of: string;
  index: number;
  born: number;
  /** When it came to rest (or ran out of time to): it fades from then. */
  settled: number | null;
}

/** The things' whole state (a kernel component). */
export interface ThingsState {
  /** Sim seconds since the scene began. */
  t: number;
  gather: GatherState;
  bag: InventoryState;
  pickups: PickupState;
  broken: Record<string, { at: number; by: "hit" | "impact" }>;
  fragments: FragmentState[];
  interaction: InteractionState;
  doors: Record<string, boolean>;
  levers: Record<string, boolean>;
  /** The sign being read, until when. */
  reading: { id: string; until: number } | null;
  /** A gather in progress: the node, its kind, when it lands, the hero's facing. */
  acting: { placement: string; kind: string; until: number; yaw: number } | null;
  nav: NavDynamicState;
  slicer: SlicerState;
  /** Breaks waiting for the break system (a blow lands inside combat's resolve; the break runs after it). */
  queued: { id: string; by: "hit" | "impact"; dir: [number, number, number]; severity: number }[];
}

/** One thing that happened (for the view, the handle and the specs; capped, not saved). */
export type ThingsEvent = { step: number; t: number } & (
  | { type: "gathered"; id: string; got: Gathered[] }
  | { type: "regrown"; id: string }
  | { type: "broken"; id: string; by: "hit" | "impact"; drops: Gathered[]; tiles: number }
  | { type: "collected"; item: string; count: number }
  | { type: "fired"; id: string; verb: string }
  | { type: "settled"; body: string }
  | { type: "faded"; body: string }
  | { type: "swapped"; tiles: number[][] }
);

/** One slice of tile work, measured (not saved). */
export interface SliceSample {
  step: number;
  ms: number;
  units: number;
  stages: string[];
  finished: number;
}

/** What the scene gives the things. */
export interface ThingsHost {
  collision: CollisionWorld;
  actions: Actions;
  rng: RngStreams;
  content: ThingsContent;
  fight: () => Fight;
  nav: NavWorld;
  hero(): ControllerState;
  groundAt(x: number, z: number): number;
  /** A streamed world: each placement's chunk. Breakables' bodies and targets and doors' slabs then load and unload with
   *  their chunk on `world:chunk`; without it every one is built at the start. */
  chunkOf?: (placement: string) => string;
  /** Phase D1: the scene's inventory, where gathered yields and collected pickups go instead of the things' own bag
   *  (src/rpg/: @rpg/items' tabbed inventory). */
  bag?: ThingsBag;
  /** Interactables owned elsewhere (phase D: the rpg layer's stations, a world addon's), each owner's entries in the
   *  same registry and chooser. (The town's townsfolk register themselves and hear presses through `Things.other`.) */
  others?: () => readonly OtherInteractables[];
}

/** An inventory the things put items into (phase D1). */
export interface ThingsBag {
  inventory: Inventory;
  state(): InventoryState;
}

/** Interactables another module owns in the things' registry (phase D: the rpg layer's stations; story's stand-in
 *  townsfolk in its lab). The things leave their entries alone; the owner keeps them current and does what a press on
 *  one does. */
export interface OtherInteractables {
  /** Register its entries (at the build). */
  register(i: Interaction): void;
  /** Update their places and switches (each step, before the chooser); `busy`: a gather holds the hero. */
  sync(i: Interaction, busy: boolean): void;
  owns(id: string): boolean;
  /** Where one stands (its feet). */
  at(id: string): [number, number, number];
  /** A press chose it. */
  fire(id: string, verb: string): void;
  /** Its kind for the standard `interact:*` events (default "station"). */
  kind?(id: string): string;
}

/** The things, for the scene, its view and the handle. */
export interface Things {
  modules: GameModule<GameEvents>[];
  build(world: World): void;
  hashed: readonly string[];
  state(): ThingsState;
  /** The bag the things fill: the scene's inventory when it gives one (the rpg layer's), else their own. */
  bag(): InventoryState;
  interaction: Interaction;
  slicer: NavTileSlicer;
  nav: NavWorld;
  content: ThingsContent;
  /** The hero's override this step (held in a gather's pose), or null. */
  heroDrive(): MoveDrive | null;
  /** Whether this step's interact press went to a thing (or would have: something is chosen). */
  claimsInteract(): boolean;
  /** The last choice (what the prompt shows). */
  choice(): InteractChoice;
  placement(id: string): Placement;
  /** Where a placement stands (its feet). */
  at(id: string): [number, number, number];
  /** A breakable's pre-cut fragments (the same the sim spawns). */
  fragmentsOf(id: string): Fragment[];
  /** A breakable placement's fighter id, and back. */
  fighterOf(id: string): number;
  /** Queue a break as a blow along `dir` would (scripted scenes, shots). */
  breakNow(id: string, dir?: [number, number, number], severity?: number): void;
  /** Throw a rock from `from` at `velocity` (a strong impact's check). Returns its body id. */
  throwRock(from: [number, number, number], velocity: [number, number, number]): string;
  /** Scatter a drop's stacks as pickups round `at` (phase D1: a defeated enemy's table), on the things' stream. */
  drop(drops: readonly Gathered[], at: [number, number, number]): void;
  events: ThingsEvent[];
  slices: SliceSample[];
  /** Interact presses fired by a spec on the next step (`true`), taken once. */
  press: boolean;
  /** Phase D (the town's NPCs): another module's interactables in the same registry (ids that are no placement). The
   *  chooser ranks them with the things; a press on one calls this, and `at` answers their registered point. */
  other: ((id: string, verb: string) => void) | null;
  steps(): number;
}

const quatYaw = (yaw: number): Quat => ({ x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) });
const rotY = (p: [number, number, number], yaw: number): [number, number, number] => {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  return [p[0] * c + p[2] * s, p[1], -p[0] * s + p[2] * c];
};

/** The convex points of a breakable kind's shape, about its feet (y up from 0). */
export function shapePoints(k: BreakableKind): [number, number, number][] {
  if (k.shape.kind === "box") {
    const [hx, hy, hz] = k.shape.half as [number, number, number];
    return boxPoints(hx, hy, hz).map((p) => [p[0], p[1] + hy, p[2]]);
  }
  return ringPoints(k.shape.profile as [number, number][], k.shape.sides);
}

/** A breakable kind's height (m). */
export const shapeHeight = (k: BreakableKind): number => (k.shape.kind === "box" ? k.shape.half[1]! * 2 : Math.max(...k.shape.profile.map((p) => p[1]!)));

/** Build the things for a scene. */
export function createThings(host: ThingsHost): Things {
  const { collision, actions, content, nav } = host;
  const dt = collision.dt;
  const random = host.rng.stream("things");
  const inventory = createInventory(content.items);
  const interaction = new Interaction(content.interact);
  const slicer = new NavTileSlicer(nav, { unitsPerSlice: content.unitsPerSlice });
  const placements = new Map(content.placements.map((p) => [p.id, p]));
  const breakableIds = content.placements.filter((p) => p.kind === "breakable").map((p) => p.id);
  const fighterOf = (id: string): number => BREAKABLE_IDS + breakableIds.indexOf(id);
  const placementOfFighter = (fid: number): string | undefined => breakableIds[fid - BREAKABLE_IDS];
  const feetOf = new Map<string, [number, number, number]>(content.placements.map((p) => [p.id, [p.pos[0]!, host.groundAt(p.pos[0]!, p.pos[1]!), p.pos[1]!]]));
  const at = (id: string): [number, number, number] => {
    const f = feetOf.get(id);
    if (f) return f;
    const owner = ownerOf(id);
    if (owner) return owner.at(id);
    // another module's interactable registered by itself (phase D: a townsperson): its registered point
    const o = interaction.get(id);
    if (o) return [o.pos.x, o.pos.y, o.pos.z];
    throw new Error(`things: no placement "${id}"`);
  };
  /** The owner of an interactable that is no placement (`ThingsHost.others`), or null (a module's that registers itself,
   *  `Things.other`: the town's townsfolk). */
  const ownerOf = (id: string): OtherInteractables | null => host.others?.().find((o) => o.owns(id)) ?? null;
  const kindOf = (id: string): BreakableKind => content.breakables.get(placements.get(id)!.piece)!;
  // every breakable cut once at load, seeded by its placement
  const frags = new Map<string, Fragment[]>(breakableIds.map((id) => [id, fracture(shapePoints(kindOf(id)), { seed: placements.get(id)!.seed, pieces: kindOf(id).pieces })]));

  const bodyOf = (id: string): string => `thing:${id}`;
  const doorBody = (id: string): string => `door:${id}`;
  // phase D1: the scene's inventory when it gives one (else the things' own bag), and `item:gained` on the bus
  const inv: Inventory = host.bag?.inventory ?? inventory;
  const bagOf = (s: ThingsState): InventoryState => host.bag?.state() ?? s.bag;
  let bus: EventBus<GameEvents> | null = null;
  const gained = (item: string, count: number, source: string): void => {
    if (count > 0) bus?.emit("item:gained", { item, count, source });
  };
  const DOOR_SLAB = { hx: 0.08, hy: 1.05, hz: 0.58 };

  /** A breakable's body: its solid shape (blocks the hero; reports impacts) and its hurt sensor (blows find it). */
  const breakableBody = (id: string): BodySpec => {
    const p = placements.get(id)!;
    const k = kindOf(id);
    const f = at(id);
    const pts = shapePoints(k).flat();
    const h = shapeHeight(k);
    const solid: ColliderSpec = { shape: { type: "convex", points: pts }, layer: "solid", tag: k.shape.kind === "box" ? "wood" : "stone", impactThreshold: k.impact };
    const hurt: ColliderSpec = { shape: { type: "cylinder", halfHeight: h / 2 + 0.05, radius: (k.shape.kind === "box" ? Math.hypot(k.shape.half[0]!, k.shape.half[2]!) : Math.max(...k.shape.profile.map((q) => q[0]!))) + 0.05 }, layer: "hurtProp", sensor: true, offset: { x: 0, y: h / 2, z: 0 } };
    return { id: bodyOf(id), kind: "fixed", position: { x: f[0], y: f[1], z: f[2] }, rotation: quatYaw(p.yaw), colliders: [solid, hurt] };
  };
  const doorSpec = (id: string): BodySpec => {
    const f = at(id);
    const p = placements.get(id)!;
    return { id: doorBody(id), kind: "fixed", position: { x: f[0], y: f[1] + DOOR_SLAB.hy, z: f[2] }, rotation: quatYaw(p.yaw), colliders: [{ shape: { type: "cuboid", halfExtents: { x: DOOR_SLAB.hz, y: DOOR_SLAB.hy, z: DOOR_SLAB.hx } }, layer: "solid", tag: "wood" }] };
  };
  let world: World | null = null;
  const state = (): ThingsState => {
    const e = world!.query(THINGS_C)[0];
    if (e === undefined) throw new Error("things: no state");
    return world!.getComponent<ThingsState>(e, THINGS_C)!;
  };
  const events: ThingsEvent[] = [];
  const slices: SliceSample[] = [];
  let steps = 0;
  const log = (ev: ThingsEvent): void => {
    events.push(ev);
    if (events.length > 600) events.splice(0, events.length - 500);
  };
  const ev = <T extends ThingsEvent["type"]>(type: T, rest: Omit<Extract<ThingsEvent, { type: T }>, "step" | "t" | "type">): void => log({ step: steps, t: state().t, type, ...rest } as unknown as ThingsEvent);

  /** The interaction registry from the placements, its switches from the state. */
  const registerAll = (): void => {
    for (const p of content.placements) {
      if (p.kind === "breakable") continue;
      const f = at(p.id);
      if (p.kind === "gatherable") {
        const g = content.gatherKinds.get(p.piece)!;
        interaction.register({ id: p.id, verb: g.verb.id, pos: { x: f[0], y: f[1], z: f[2] }, radius: g.reach, priority: g.priority });
      } else {
        const k = content.interactables.get(p.piece)!;
        interaction.register({ id: p.id, verb: k.verb.id, pos: { x: f[0], y: f[1], z: f[2] }, radius: k.reach, priority: k.priority, ...(k.cone !== undefined ? { cone: { yaw: p.yaw, half: (k.cone * Math.PI) / 180 } } : {}) });
      }
    }
  };
  /** Each step: what can be used now (a gathered node can't; a used door or lever offers its other verb). */
  const syncRegistry = (s: ThingsState): void => {
    for (const th of interaction.list()) {
      const p = placements.get(th.id);
      // another module's entry (phase D: a townsperson): its owner keeps it current
      if (!p) continue;
      const patch: Partial<Interactable> = {};
      if (p.kind === "gatherable") patch.enabled = content.gathering.ready(s.gather, p.id) && s.acting === null;
      else {
        const k = content.interactables.get(p.piece)!;
        const used = k.kind === "door" ? s.doors[p.id] === true : k.kind === "lever" ? s.levers[p.id] === true : false;
        patch.verb = used && k.verbAlt ? k.verbAlt.id : k.verb.id;
        patch.enabled = s.acting === null;
      }
      interaction.set(th.id, patch);
    }
    for (const o of host.others?.() ?? []) o.sync(interaction, s.acting !== null);
  };
  const setNavDynamic = (s: ThingsState, id: string, on: boolean): number => {
    const tiles = nav.setDynamic(id, on);
    slicer.request(tiles);
    s.nav = nav.dynamicState();
    s.slicer = slicer.snapshot();
    return tiles.length;
  };

  // the standard interact events (@rpg-unit/interact-prompt, @rpg/contracts): `interact:request` when a press fires a
  // thing, `interact:done` once it acted (a sign read, a lever pulled, a door swung, a node gathered, a townsperson or
  // station answered); kinds: the interactable's kind, "gatherable", an owner's own ("station" by default), "npc"
  const emit: InteractEmit = (name, payload) => bus?.emit(name, payload);
  const interactKind = (id: string): string => {
    const p = placements.get(id);
    if (!p) {
      const owner = ownerOf(id);
      return owner ? (owner.kind?.(id) ?? "station") : "npc";
    }
    return p.kind === "gatherable" ? "gatherable" : (content.interactables.get(p.piece)?.kind ?? p.kind);
  };
  const done = (id: string): void => interactDone({ emit, actor: HERO_ID, target: id, kind: interactKind(id) });

  const fire = (s: ThingsState, id: string, verb: string): void => {
    const p = placements.get(id);
    ev("fired", { id, verb });
    if (!p) {
      const owner = ownerOf(id);
      if (owner) owner.fire(id, verb);
      else things.other?.(id, verb);
      if (owner || things.other) done(id);
      return;
    }
    const f = at(id);
    const h = host.hero();
    if (p.kind === "gatherable") {
      const g = content.gatherKinds.get(p.piece)!;
      const yaw = Math.atan2(f[0] - h.feet.x, f[2] - h.feet.z);
      s.acting = { placement: id, kind: p.piece, until: s.t + g.seconds, yaw };
      host.fight().bodies.play(HERO_ID, g.pose.id);
      return;
    }
    const k = content.interactables.get(p.piece)!;
    if (k.kind === "sign") s.reading = { id, until: s.t + 5 };
    else if (k.kind === "lever") {
      s.levers[id] = !s.levers[id];
      host.fight().bodies.play(HERO_ID, "use");
    } else if (k.kind === "door") {
      const open = !s.doors[id];
      s.doors[id] = open;
      if (open) collision.remove(doorBody(id));
      else if (!collision.has(doorBody(id))) collision.add(doorSpec(id));
      setNavDynamic(s, `door:${id}`, !open);
      host.fight().bodies.play(HERO_ID, "use");
    }
    done(id);
  };

  const finishGather = (s: ThingsState): void => {
    const a = s.acting!;
    s.acting = null;
    const got = content.gathering.gather(s.gather, a.placement, a.kind, s.t, () => random.next());
    if (!got) return;
    done(a.placement);
    const spill: Gathered[] = [];
    for (const g of got) {
      const left = inv.add(bagOf(s), g.item, g.count);
      gained(g.item, g.count - left, "gather");
      if (left > 0) spill.push({ item: g.item, count: left });
    }
    // what doesn't fit the bag drops at the node
    if (spill.length) spawnPickups(s.pickups, spill, at(a.placement), s.t, content.pickups, () => random.next(), host.groundAt);
    ev("gathered", { id: a.placement, got });
  };

  const doBreak = (s: ThingsState, q: ThingsState["queued"][number]): void => {
    if (s.broken[q.id]) return;
    const id = q.id;
    const k = kindOf(id);
    const p = placements.get(id)!;
    const f = at(id);
    s.broken[id] = { at: s.t, by: q.by };
    if (collision.has(bodyOf(id))) collision.remove(bodyOf(id));
    const fight = host.fight();
    if (fight.combat.has(fighterOf(id))) fight.combat.removeFighter(fighterOf(id));
    // the pre-cut fragments become rigid bodies, flung out from the centre and along the blow
    const h = shapeHeight(k);
    frags.get(id)!.forEach((fr, i) => {
      const c = rotY(fr.centre, p.yaw);
      const out = Math.hypot(c[0], c[2]) > 1e-6 ? [c[0] / Math.hypot(c[0], c[2]), c[2] / Math.hypot(c[0], c[2])] : [0, 0];
      const body = `frag:${id}:${i}`;
      collision.add({
        id: body,
        kind: "dynamic",
        position: { x: f[0] + c[0], y: f[1] + c[1], z: f[2] + c[2] },
        rotation: quatYaw(p.yaw),
        colliders: [{ shape: { type: "convex", points: fr.points.flat() }, layer: "debris", density: k.density, friction: 0.8, restitution: 0.15 }],
        linearDamping: 0.15,
        angularDamping: 0.6,
      });
      const push = 2.5 * q.severity;
      const up = 1.2 + (c[1] / Math.max(0.1, h)) * 1.5;
      collision.setVelocity(
        body,
        { x: out[0]! * k.burst + q.dir[0] * push, y: up, z: out[1]! * k.burst + q.dir[2] * push },
        { x: (random.next() * 2 - 1) * 5, y: (random.next() * 2 - 1) * 5, z: (random.next() * 2 - 1) * 5 },
      );
      s.fragments.push({ body, of: id, index: i, born: s.t, settled: null });
    });
    const drops = rollDrops(content.drops.get(k.drops.id)!, () => random.next());
    spawnPickups(s.pickups, drops, [f[0], f[1] + h * 0.5, f[2]], s.t, content.pickups, () => random.next(), host.groundAt);
    const tiles = k.blocksNav ? setNavDynamic(s, bodyOf(id), false) : 0;
    ev("broken", { id, by: q.by, drops, tiles });
  };

  const applyFaults = (): void => {
    slicer.faults.oneSlice = THINGS_FAULTS.navOneSlice;
    interaction.faults.ignorePriority = THINGS_FAULTS.interactIgnorePriority;
    content.gathering.faults.neverRegrow = THINGS_FAULTS.gatherNeverRegrow;
  };

  /** A placement's bodies and combat target, as its state says (a broken breakable has none; an open door no slab). */
  const loadPlacement = (s: ThingsState | null, id: string): void => {
    const p = placements.get(id)!;
    if (p.kind === "breakable") {
      if (s?.broken[id]) return;
      if (!collision.has(bodyOf(id))) collision.add(breakableBody(id));
      const fight = host.fight();
      if (!fight.combat.has(fighterOf(id))) fight.combat.addFighter(fighterOf(id), { team: "prop", body: bodyOf(id), hurtLayer: "hurtProp", weapon: null, stats: { hpMax: 1, hp: 1, poiseMax: 1, poise: 1, attack: 0, defence: 0 } });
    } else if (p.kind === "interactable" && content.interactables.get(p.piece)!.kind === "door") {
      if (!s?.doors[id] && !collision.has(doorBody(id))) collision.add(doorSpec(id));
    }
  };
  const unloadPlacement = (s: ThingsState, id: string): void => {
    const p = placements.get(id)!;
    if (p.kind === "breakable") {
      if (collision.has(bodyOf(id))) collision.remove(bodyOf(id));
      const fight = host.fight();
      if (fight.combat.has(fighterOf(id))) fight.combat.removeFighter(fighterOf(id));
      s.fragments = s.fragments.filter((fr) => {
        if (fr.of !== id) return true;
        if (collision.has(fr.body)) collision.remove(fr.body);
        return false;
      });
    } else if (collision.has(doorBody(id))) collision.remove(doorBody(id));
  };
  /** Streamed: the chunks loaded so far (heard before the build too: the world primes its chunks first). */
  const loaded = new Set<string>();
  const inChunk = (chunk: string): string[] => (host.chunkOf ? content.placements.filter((p) => host.chunkOf!(p.id) === chunk).map((p) => p.id) : []);

  let claimed = false;
  const things: Things = {
    modules: [],
    build(w) {
      world = w;
      const s: ThingsState = {
        t: 0,
        gather: { nodes: {} },
        bag: emptyInventory(content.maxStacks),
        pickups: emptyPickups(),
        broken: {},
        fragments: [],
        interaction: { current: null, fired: 0 },
        doors: {},
        levers: {},
        reading: null,
        acting: null,
        nav: nav.dynamicState(),
        slicer: slicer.snapshot(),
        queued: [],
      };
      w.addComponent<ThingsState>(w.createEntity(), THINGS_C, s);
      for (const p of content.placements) if (!host.chunkOf || loaded.has(host.chunkOf(p.id))) loadPlacement(s, p.id);
      registerAll();
      for (const o of host.others?.() ?? []) o.register(interaction);
    },
    hashed: [THINGS_C],
    state,
    bag: () => bagOf(state()),
    interaction,
    slicer,
    nav,
    content,
    heroDrive: () => {
      if (!world) return null;
      const a = state().acting;
      return a ? { x: 0, z: 0, yaw: a.yaw } : host.fight().heroDrive();
    },
    claimsInteract: () => claimed,
    choice: () => interaction.choice(),
    placement: (id) => {
      const p = placements.get(id);
      if (!p) throw new Error(`things: no placement "${id}"`);
      return p;
    },
    at,
    fragmentsOf: (id) => frags.get(id) ?? [],
    fighterOf,
    breakNow: (id, dir = [0, 0, -1], severity = 0.8) => void state().queued.push({ id, by: "hit", dir, severity }),
    throwRock(from, velocity) {
      const id = `rock:${steps}`;
      collision.add({ id, kind: "dynamic", position: { x: from[0], y: from[1], z: from[2] }, colliders: [{ shape: { type: "ball", radius: 0.18 }, layer: "debris", density: 1200 }], ccd: true });
      collision.setVelocity(id, { x: velocity[0], y: velocity[1], z: velocity[2] });
      return id;
    },
    drop(drops, at) {
      const s = state();
      spawnPickups(s.pickups, drops, at, s.t, content.pickups, () => random.next(), host.groundAt);
    },
    events,
    slices,
    press: false,
    other: null,
    steps: () => steps,
  };

  const module: GameModule<GameEvents> = {
    id: "things",
    version: "0.1.0",
    // checked declarations (decision 0077): what it emits
    provides: { events: ["item:gained", "interact:request", "interact:done"] },
    setup(ctx) {
      bus = ctx.events;
      // a streamed world: a chunk's things come and go with it (before the build, only noted)
      if (host.chunkOf)
        ctx.events.on("world:chunk", (e) => {
          if (e.state === "loaded") loaded.add(e.id);
          else loaded.delete(e.id);
          if (!world) return;
          const s = state();
          for (const id of inChunk(e.id)) if (e.state === "loaded") loadPlacement(s, id);
          else unloadPlacement(s, id);
        });
      // a blow on a breakable: combat says it was defeated (its health is 1); the break runs after combat's resolve
      ctx.events.on("combat:defeated", (p) => {
        const id = placementOfFighter(p.entity);
        if (id === undefined || !world) return;
        const s = state();
        if (!s.broken[id] && !s.queued.some((q) => q.id === id)) s.queued.push({ id, by: "hit", dir: [...p.dir] as [number, number, number], severity: p.severity });
      });
      ctx.registerSystem(
        ({ dt: stepDt }) => {
          steps++;
          applyFaults();
          state().t += stepDt;
        },
        { name: "things:clock", order: -40 },
      );
      ctx.registerSystem(
        () => {
          const s = state();
          for (const id of content.gathering.step(s.gather, s.t)) ev("regrown", { id });
        },
        { name: "things:regrow", order: -30 },
      );
      ctx.registerSystem(
        () => {
          const s = state();
          claimed = false;
          const pressed = things.press || actions.pressed("interact");
          things.press = false;
          if (s.reading && s.t >= s.reading.until) s.reading = null;
          if (s.acting) {
            if (s.t >= s.acting.until - 1e-9) finishGather(s);
            claimed = pressed;
            syncRegistry(s);
            const h = host.hero();
            interaction.update({ pos: h.feet, yaw: h.yaw }, s.interaction);
            return;
          }
          syncRegistry(s);
          const h = host.hero();
          // choose and fire (@rpg-unit/interact-prompt): `interact:request` goes out for what fired
          const step = interactStep({ interaction, state: s.interaction, at: { pos: h.feet, yaw: h.yaw }, pressed, actor: HERO_ID, kindOf: interactKind, emit });
          claimed = step.claimed;
          if (step.fired) fire(s, step.fired.id, step.fired.verb);
        },
        { name: "things:interact", order: -20 },
      );
      ctx.registerSystem(
        () => {
          const s = state();
          const h = host.hero();
          for (const c of collectPickups(s.pickups, [h.feet.x, h.feet.y, h.feet.z], s.t, content.pickups, inv, bagOf(s))) {
            ev("collected", { item: c.item, count: c.count });
            gained(c.item, c.count, "pickup");
          }
        },
        { name: "things:pickups", order: 20 },
      );
      ctx.registerSystem(
        () => {
          const s = state();
          // strong impacts on standing breakables (a thrown rock, a falling crate's piece)
          for (const im of collision.impacts()) {
            for (const [mine, other] of [
              [im.a, im.b],
              [im.b, im.a],
            ] as const) {
              // a breakable's own flying pieces never break its neighbours (no chain through the row)
              if (!mine.startsWith("thing:") || other.startsWith("frag:")) continue;
              const id = mine.slice("thing:".length);
              if (s.broken[id] || s.queued.some((q) => q.id === id)) continue;
              let dir: [number, number, number] = [0, 0, -1];
              if (collision.has(other)) {
                const v = collision.velocity(other).linear;
                const l = Math.hypot(v.x, v.z);
                if (l > 1e-6) dir = [v.x / l, 0, v.z / l];
              }
              s.queued.push({ id, by: "impact", dir, severity: Math.min(1, im.force / (kindOf(id).impact * 4)) });
            }
          }
          for (const q of s.queued.splice(0)) doBreak(s, q);
          // fragments: settle (at rest, or out of time), fade, go
          for (const fr of s.fragments) {
            if (fr.settled !== null || !collision.has(fr.body)) continue;
            const k = kindOf(fr.of);
            const v = collision.velocity(fr.body);
            const rest = Math.hypot(v.linear.x, v.linear.y, v.linear.z) < 0.08 && Math.hypot(v.angular.x, v.angular.y, v.angular.z) < 0.4;
            if ((rest && s.t - fr.born > 0.3) || s.t - fr.born >= k.settle) {
              fr.settled = s.t;
              ev("settled", { body: fr.body });
            }
          }
          s.fragments = s.fragments.filter((fr) => {
            if (fr.settled === null || s.t - fr.settled < kindOf(fr.of).fade - 1e-9) return true;
            collision.remove(fr.body);
            ev("faded", { body: fr.body });
            return false;
          });
        },
        { name: "things:break", order: 160 },
      );
      ctx.registerSystem(
        () => {
          const s = state();
          if (!slicer.busy()) return;
          const r = slicer.step();
          slices.push({ step: steps, ms: r.ms, units: r.units, stages: r.stages, finished: r.finished.length });
          if (slices.length > 4000) slices.splice(0, slices.length - 3000);
          s.slicer = slicer.snapshot();
          if (r.finished.length) {
            s.nav = nav.dynamicState();
            ev("swapped", { tiles: r.finished.map((t) => [t.x, t.y]) });
          }
        },
        { name: "things:nav", order: 170 },
      );
      ctx.registerSaveSlice({
        serialize: () => structuredClone(state()),
        deserialize: (data) => {
          const d = structuredClone(data as ThingsState);
          const s = state();
          for (const k of Object.keys(s)) delete (s as unknown as Record<string, unknown>)[k];
          Object.assign(s, d);
          nav.restoreDynamic(s.nav);
          slicer.restore(s.slicer);
          syncRegistry(s);
        },
      });
    },
  };
  things.modules = [module];
  return things;
}

/** The dynamic nav shapes a placement list carves at load: blocking breakables standing, doors closed. */
export function dynamicNavShapes(content: ThingsContent, groundAt: (x: number, z: number) => number): Record<string, NavShape[]> {
  const out: Record<string, NavShape[]> = {};
  for (const p of content.placements) {
    const y = groundAt(p.pos[0]!, p.pos[1]!);
    const q = quatYaw(p.yaw);
    if (p.kind === "breakable") {
      const k = content.breakables.get(p.piece)!;
      if (!k.blocksNav) continue;
      const half: [number, number, number] = k.shape.kind === "box" ? [k.shape.half[0]!, k.shape.half[1]!, k.shape.half[2]!] : [0.3, shapeHeight(k) / 2, 0.3];
      out[`thing:${p.id}`] = [{ kind: "box", center: [p.pos[0]!, y + half[1], p.pos[1]!], halfExtents: half, rotation: q }];
    } else if (p.kind === "interactable" && content.interactables.get(p.piece)!.kind === "door") {
      out[`door:${p.id}`] = [{ kind: "box", center: [p.pos[0]!, y + 1.05, p.pos[1]!], halfExtents: [0.58, 1.05, 0.08], rotation: q }];
    }
  }
  return out;
}
